import React from 'react';
import cn from 'classnames';

import { Quote, File, Arrow } from '@/icons';

import styles from './slider.module.scss';

interface ILink {
  name: string;
  type: string;
  url: string;
}

export interface ISlide {
  id: number;
  name: string;
  company: string;
  text?: string | string[];
  cityCount: number;
  carsCount: number;
  link?: ILink;
}

interface IProps {
  items: ISlide[];
  activeSlide: number;
  handlePrev: () => void;
  handleNext: () => void;
  handleSlide: (active: number) => void;
}

const Slider: React.FC<IProps> = (props) => {
  const { items, activeSlide, handlePrev, handleNext, handleSlide } = props;

  const mainSLide = items.find((item) => item.id === activeSlide);
  const subSlide = items.find((item) =>
    activeSlide !== items.length - 1
      ? item.id === activeSlide + 1
      : item.id === 0
  );

  const cutText = (text: string): string => {
    if (text && text.length > 250) {
      return `${text?.substring(0, 250)}...`;
    }

    return text;
  };

  return (
    <div className={styles.slider}>
      <div className={styles.slider__main}>
        <div className={styles.slider__header}>
          <div>
            <p className={styles.slider__name}>{mainSLide?.name}</p>
            <p className={styles.slider__company}>{mainSLide?.company}</p>
          </div>
          <div className={styles.slider__column}>
            <span className={styles.slider__count}>
              {`${mainSLide?.cityCount} городов`}
            </span>
            <span
              className={styles.slider__count}
            >{`${mainSLide?.carsCount} авто`}</span>
          </div>
        </div>
        {mainSLide?.text && (
          <>
            {typeof mainSLide?.text === 'object' ? (
              <>
                {mainSLide?.text.map((text) => (
                  <p className={styles.slider__text}>{text}</p>
                ))}
              </>
            ) : (
              <p className={styles.slider__text}>{mainSLide?.text}</p>
            )}
          </>
        )}
        {mainSLide?.link && (
          <a
            href={mainSLide?.link.url}
            download={mainSLide?.link.url}
            target='_blank'
            rel='noreferrer'
            className={styles.slider__link}
          >
            <File className={styles.slider__file} />
            <div className={styles.slider__linkDescription}>
              <p className={styles.slider__company}>{mainSLide?.link.name}</p>
              <p className={styles.slider__type}>{mainSLide?.link.type}</p>
            </div>
            <Arrow className={styles.slider__linkArrow} />
          </a>
        )}
        <Quote className={styles.slider__icon} />
      </div>
      <div className={styles.slider__actions}>
        <button
          className={styles.slider__button}
          type='button'
          onClick={handlePrev}
        >
          <Arrow className={styles.slider__buttonPrev} />
        </button>
        <div className={styles.slider__dots}>
          {items.map((item) => {
            const dotClassName = cn(styles.slider__dot, {
              [styles.slider__dot_active]: item.id === activeSlide
            });

            return (
              <button
                key={item.id}
                className={dotClassName}
                type='button'
                onClick={() => handleSlide(item.id)}
              />
            );
          })}
        </div>
        <button
          className={styles.slider__button}
          type='button'
          onClick={handleNext}
        >
          <Arrow className={styles.slider__buttonNext} />
        </button>
      </div>
      <div className={styles.slider__mainSmall}>
        <div className={styles.slider__sub}>
          <div className={styles.slider__header}>
            <div>
              <p className={styles.slider__name}>{subSlide?.name}</p>
              <p className={styles.slider__company}>{subSlide?.company}</p>
            </div>
            <div className={styles.slider__column}>
              <span className={styles.slider__count}>
                {`${subSlide?.cityCount} городов`}
              </span>
              <span
                className={styles.slider__count}
              >{`${subSlide?.carsCount} авто`}</span>
            </div>
          </div>
          {subSlide?.text && (
            <p className={styles.slider__text}>
              {typeof subSlide?.text === 'object'
                ? cutText(subSlide?.text[0])
                : cutText(subSlide?.text)}
            </p>
          )}
          {subSlide?.link && (
            <a
              href={subSlide?.link.url}
              download={subSlide?.link.url}
              target='_blank'
              rel='noreferrer'
              className={styles.slider__linkSmall}
            >
              <File className={styles.slider__fileSmall} />
            </a>
          )}
        </div>
        <div className={styles.slider__buttons}>
          <button
            className={styles.slider__button}
            type='button'
            onClick={handlePrev}
          >
            <Arrow className={styles.slider__buttonPrev} />
          </button>
          <button
            className={styles.slider__button}
            type='button'
            onClick={handleNext}
          >
            <Arrow className={styles.slider__buttonNext} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Slider;
